import { dutchAuctionPriceSats, type AuctionRecord } from "../../lib/auctions";

type DutchTerms = Pick<AuctionRecord, "startPriceSats" | "floorPriceSats" | "startsAt" | "endsAt" | "status" | "settlementState" | "highBid" | "updatedAt">;
const MINUTE = 60_000;

/** Display only. Purchases still use the existing exact, expiring Worker quote. */
export function dutchAuctionPresentation(auction: DutchTerms, now: number) {
  const scheduled = now < auction.startsAt;
  const active = auction.status === "open" && auction.settlementState === "open" && now < auction.endsAt && !auction.highBid;
  const stoppedAt = auction.highBid?.createdAt ?? (auction.status === "open" && auction.settlementState === "open"
    ? now : Math.min(now, auction.updatedAt));
  const price = auction.highBid?.bidSats ?? dutchAuctionPriceSats(auction, stoppedAt);
  const steps = Math.max(1, Math.ceil((auction.endsAt - auction.startsAt) / MINUTE) - 1);
  const spread = auction.startPriceSats - auction.floorPriceSats!;
  const floorAt = auction.startsAt + steps * MINUTE;
  let nextDrop: { at: number; price: number } | null = null;
  if (active && !scheduled && spread > 0 && price > auction.floorPriceSats!) {
    // A tiny spread can round to the same sat for many ticks. Show the next
    // actual price change, not a countdown to a drop that does not exist.
    const nextReduction = BigInt(auction.startPriceSats - price + 1);
    const nextStep = Number((nextReduction * BigInt(steps) + BigInt(spread) - 1n) / BigInt(spread));
    const at = auction.startsAt + nextStep * MINUTE;
    if (at < auction.endsAt) nextDrop = { at, price: dutchAuctionPriceSats(auction, at) };
  }
  const phase = auction.highBid ? "accepted" : !active ? "closed" : scheduled ? "scheduled" : price === auction.floorPriceSats ? "floor" : "live";
  const cursorAt = Math.min(auction.endsAt, Math.max(auction.startsAt, stoppedAt));
  const duration = auction.endsAt - auction.startsAt;
  const point = (at: number, value: number) => ({
    x: 8 + 584 * (at - auction.startsAt) / duration,
    y: 12 + 92 * (spread ? (auction.startPriceSats - value) / spread : 1),
  });
  // A bounded overview, sampled at real minute ticks using the shared price
  // function. Exact upcoming prices/times are listed separately below it.
  const samples = Math.min(40, steps);
  const times = new Set([auction.startsAt, ...Array.from({ length: samples }, (_, i) =>
    Math.min(auction.endsAt - 1, auction.startsAt + Math.floor(steps * (i + 1) / samples) * MINUTE)), auction.endsAt - 1]);
  const points = [...times].sort((a, b) => a - b).map(at => point(at, dutchAuctionPriceSats(auction, at)));
  const path = points.map(({ x, y }, i) => `${i ? "L" : "M"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  return { phase, price, nextDrop, floorAt, cursor: point(cursorAt, price), path, active };
}

export function dutchCountdown(until: number, now: number) {
  const seconds = Math.max(0, Math.ceil((until - now) / 1_000));
  if (seconds >= 86_400) return `${Math.floor(seconds / 86_400)}d ${Math.floor(seconds % 86_400 / 3_600)}h`;
  if (seconds >= 3_600) return `${Math.floor(seconds / 3_600)}h ${String(Math.floor(seconds % 3_600 / 60)).padStart(2, "0")}m`;
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}
